import { IkeUnit } from '@ikegps/core-measurement';
import { type CaptureFileJSONObject, IkeToken } from '@ikegps/core-api';
import { action, observable, makeObservable } from 'mobx';
import { IMithrilComponent } from '../../components/mithril.component';
import { addStyles, assetPath, DefaultTheme } from '../../components/theme';
import { m } from '../../mithril';
import { Services } from '../../services';

const CSS = {
	container: 'c-PhotomeasureOverlay',
	orca: 'c-PhotomeasureOverlay__device',
	orcaIcon: 'c-PhotomeasureOverlay__orcaIcon',
	fov: 'c-PhotomeasureOverlay__fov',
	fovSprite: 'c-PhotomeasureOverlay__fovSprite',
	label: 'c-PhotomeasureOverlay__label',
};

const Style = {
	[CSS.container]: {
		position: 'absolute',
	},

	[CSS.orca]: {
		position: 'absolute',
		transform: 'translate3d(-16px, -16px, -1px)',
		width: '32px',
		height: '32px',
		borderRadius: '16px',
		boxShadow: DefaultTheme.shadow.box.dp4,
	},

	[CSS.orcaIcon]: {
		width: '32px',
		height: '32px',
		transformOrigin: '50% 50%',
	},

	[CSS.fov]: {
		position: 'absolute',
		left: 0,
		top: 0,
		transform: 'translate3d(-75px, -129px, -1px)',
	},

	[CSS.fovSprite]: {
		width: '150px',
		height: '129px',
		transformOrigin: '50% 100%',
	},

	[CSS.label]: {
		paddingTop: '20px',
	},
};

addStyles(Style);

interface IPhotomeasureMapOverlayOptions {
	capture?: CaptureFileJSONObject;
}

export class PhotomeasureMapOverlay extends IMithrilComponent {
	private overlay: google.maps.OverlayView;
	private el: Element;

	name = 'PhotomeasureMapOverlay';

	@observable capture: CaptureFileJSONObject | null = null;

	constructor(options: IPhotomeasureMapOverlayOptions) {
		super();

		makeObservable(this);

		this.overlay = new google.maps.OverlayView();
		this.capture = options.capture || null;

		this.overlay.onAdd = () => {
			this.el = this.overlay.getPanes().overlayLayer;
			Services.Render.mount(this.el, this, this.name);
		};

		this.overlay.onRemove = () => {
			if (this.el) {
				Services.Render.mount(this.el, null, this.name);
			}
		};

		this.overlay.draw = this.onDraw.bind(this);

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		Services.Map.isReadyPromise.then(() => {
			this.overlay.setMap(Services.Map.map);
		});
	}

	@action public setCapture(capture: CaptureFileJSONObject) {
		this.capture = capture;

		this.onDraw();
	}

	override onRemove() {
		if (this.el) {
			Services.Render.mount(this.el, null, this.name);
		}
	}

	onDraw() {
		if (this.el) {
			Services.Render.render(this.el, this.view());
		}
	}

	canSeeFieldOfView(): boolean {
		return Services.Token.has(IkeToken.FEATURE_MAP_FIELD_OF_VIEW);
	}

	get nodes() {
		const projection = this.overlay.getProjection();

		if (projection && this.capture && this.capture.device && this.canSeeFieldOfView()) {
			const { latitude, longitude } = this.capture.device;

			const point = projection.fromLatLngToDivPixel(
				new google.maps.LatLng({
					lat: latitude,
					lng: longitude,
				})
			);

			return m(
				'div',
				{
					className: CSS.container,
					style: {
						left: `${point.x}px`,
						top: `${point.y}px`,
					},
				},
				[
					m(
						'div',
						{
							className: CSS.fov,
						},
						m('img', {
							src: assetPath('/images/fov-cone.svg'),
							className: CSS.fovSprite,
							style: {
								transform: `rotate(${this.capture.orientation.bearing}rad)`,
							},
						})
					),
					m(
						'div',
						{
							className: CSS.orca,
						},
						m('img', {
							src: assetPath('/images/ike-device.svg'),
							className: CSS.orcaIcon,
							style: {
								transform: `rotate(${this.capture.orientation.bearing}rad)`,
							},
						})
					),
					m(
						'div',
						{
							className: CSS.label,
						},
						IkeUnit.display(this.capture.distance, Services.UserSettings.units)
					),
				]
			);
		}

		return null;
	}
}
